import { Fragment, useEffect, useCallback, useState } from "react";
import { TableRowLoader } from "components/loaders";
import {
  CusomerDefaultFilters,
  CustomerDefaultMultiFilter,
} from "content/DefaultFilter";
import { CustomerTableColumns } from "content/TableColumns";
import { sortOptions } from "content/SortDropdown";
import * as Components from "components";
import {
  debounce,
  downloadBlob,
  formatPhoneNumberUS,
  formatThousand,
} from "utils";
import useFetch from "hooks/useFetch";
import * as Icons from "assets/svg";
import { CustomerReportDropdownOptions } from "content/Select";
import { toast } from "react-toastify";
import apiClient from "config/api";
import { useLocation } from "react-router";

const CustomerReports = () => {
  const [checkedTableMenuColumn, setCheckedTableMenuColumn] =
    useState(CustomerTableColumns);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [filter, setFilter] = useState(CusomerDefaultFilters);
  const [search, setSearch] = useState("");
  const [isRerender, setIsRerender] = useState(false);
  const [exportValue, setExportValue] = useState("excel");
  const { data, loading } = useFetch(
    `/admin/reports/customer-reports`,
    filter,
    [filter, isRerender]
  );
  const location = useLocation();
  const { startDate, endDate, plan } = location.state || {
    startDate: null,
    endDate: null,
  };

  useEffect(() => {
    if (startDate && endDate && plan) {
      setFilter((prev) => ({
        ...prev,
        fromDate: startDate,
        toDate: endDate,
        plan: plan,
        page: 1,
      }));
      setIsRerender((prev) => !prev);
    } else if (startDate && endDate) {
      setFilter((prev) => ({
        ...prev,
        fromDate: startDate,
        toDate: endDate,
        page: 1,
      }));
      setIsRerender((prev) => !prev);
    }
  }, [startDate, endDate, plan]);

  // Memoize the debouncedSearch function using useCallback
  const debouncedSearch = useCallback(
    debounce((value) => {
      handleFilter("search", value);
    }, 500),
    []
  );

  // Handle Search change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    debouncedSearch(value);
  };

  const handleFilter = (name, value) => {
    if (name === "filter") {
      setFilter((prev) => ({
        ...prev,
        ...value,
        page: 1,
      }));
    } else if (name === "pagination") {
      setFilter((prev) => ({
        ...prev,
        page: value.current,
        limit: value.pageSize,
      }));
    } else {
      setFilter((prev) => ({
        ...prev,
        [name]: value,
        page: 1,
      }));
    }
    setSelectedRowKeys([]);
  };

  const handleFilterChange = (selectedFilters) => {
    const combinedFilters = Object.values(selectedFilters).flat();

    const formatedFilters = combinedFilters.reduce(
      (acc, item) => {
        if (
          item === "Trial" ||
          item === "Basic" ||
          item === "Pro" ||
          item === "Premium"
        ) {
          acc.plan = acc.plan ? `${acc.plan},${item}` : item;
        } else if (item === "monthly" || item === "yearly") {
          acc.billingCycle = acc.billingCycle
            ? `${acc.billingCycle},${item}`
            : item;
        } else if (
          item === "active" ||
          item === "canceled" ||
          item === "past_due" ||
          item === "incomplete" ||
          item === "incomplete_expired"
        ) {
          acc.status = acc.status ? `${acc.status},${item}` : item;
        } else if (item.fromDate || item.toDate) {
          acc.fromDate = item.fromDate;
          acc.toDate = item.toDate;
        }

        return acc;
      },
      { plan: "", billingCycle: "", status: "", fromDate: "", toDate: "" }
    );
    // update filters
    handleFilter("filter", formatedFilters);
  };

  const handleSelectChange = async (value) => {
    setExportValue(value);
  };

  const handleExport = async () => {
    if (exportValue === "excel") {
      const toastId = toast.loading("Preparing Excel export...");
      try {
        const response = await apiClient.get(
          "/admin/reports/customer-reports/export-excel",
          {
            params: filter,
            responseType: "arraybuffer",
          }
        );
        if (response?.data) {
          toast.update(toastId, {
            render: "Excel export started successfully!",
            type: "success",
            isLoading: false,
            autoClose: 3000,
          });

          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          downloadBlob(blob, "customers-report.xlsx");
        } else {
          toast.error("No data received from the API.");
        }
      } catch (error) {
        toast.update(toastId, {
          render: error.message || "Failed to export data",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
      }
    } else if (exportValue === "pdf") {
      const toastId = toast.loading("Preparing PDF export...");
      try {
        const response = await apiClient.get(
          "/admin/reports/customer-reports/export-pdf",
          { params: filter, responseType: "arraybuffer" }
        );
        if (response?.data) {
          toast.update(toastId, {
            render: "PDF export started successfully!",
            type: "success",
            isLoading: false,
            autoClose: 3000,
          });

          const blob = new Blob([response.data], {
            type: "application/pdf",
          });
          downloadBlob(blob, "customers-report.pdf");
        } else {
          toast.error("No data received from the API.");
        }
      } catch (error) {
        toast.update(toastId, {
          render: error.message || "Failed to export data",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
      }
    }
  };

  const onRowSelectionChange = (selectedKeys) => {
    setSelectedRowKeys(selectedKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onRowSelectionChange,
  };

  const columns = [
    {
      title: "NAME",
      dataIndex: "name",
      loader: <TableRowLoader />,
      render: (_, record) => {
        const { firstName, lastName } = record;
        return <div>{`${firstName} ${lastName}`}</div>;
      },
    },
    {
      title: "CONTACT INFORMATION",
      dataIndex: "contactInfo",
      loader: <TableRowLoader />,
      render: (_, record) => {
        const { phone, email } = record;
        return (
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-3">
              <img
                src={Icons.MobilePhoneIcon}
                alt=""
                className="size-5 shrink-0"
              />
              <span>{formatPhoneNumberUS(phone)}</span>{" "}
            </div>
            <div className="flex items-center gap-3">
              <img src={Icons.EmailIcon} alt="" className="size-5 shrink-0" />
              <span>{email}</span>
            </div>
          </div>
        );
      },
    },
    {
      title: "SUBSCRIPTION",
      dataIndex: "subscription",
      loader: <TableRowLoader />,
      render: (_, record) => {
        if (record.plan.name === "Basic") {
          return (
            <div className="w-fit px-3 py-1 rounded-full bg-alpha/10">
              <div className="text-alpha">Basic</div>
            </div>
          );
        } else if (record.plan.name === "Pro") {
          return (
            <div className="w-fit px-3 py-1 rounded-full bg-secondary/10">
              <div className="text-secondary">Pro</div>
            </div>
          );
        } else if (record.plan.name === "Premium") {
          return (
            <div className="w-fit px-3 py-1 rounded-full bg-primary/10">
              <div className="text-primary">Premium</div>
            </div>
          );
        }
        return (
          <div className="w-fit px-3 py-1 rounded-full bg-red-50">
            <div className="text-red-500">Trial</div>
          </div>
        );
      },
    },
    {
      title: "AMOUNT",
      dataIndex: "amount",
      loader: <TableRowLoader />,
      render: (_, record) => {
        return record.plan.name && record.plan.name !== "Trial" ? (
          <div>
            <span>${formatThousand(record.plan.amount)}</span> /{" "}
            <span>
              Per {record.plan.billingCycle === "yearly" ? "Year" : "Monthly"}
            </span>
          </div>
        ) : (
          <div>--</div>
        );
      },
    },
    {
      title: "STATUS",
      dataIndex: "status",
      loader: <TableRowLoader />,
      render: (_, record) => {
        const status = record.plan.status;

        switch (status) {
          case "expired":
            return (
              <div>
                <div className="text-red-500">Expired</div>
                <button className="text-xs font-semibold text-secondary">
                  Renew Now
                </button>
              </div>
            );

          case "canceled":
            return (
              <div>
                <div className="text-orange-500">Cancelled</div>
                <button className="text-xs font-semibold text-secondary">
                  Subscribe Again
                </button>
              </div>
            );

          case "inactive":
            return (
              <div>
                <div className="text-gray-500">Inactive</div>
                <button className="text-xs font-semibold text-secondary">
                  Activate
                </button>
              </div>
            );

          case "trial":
            // Calculate days remaining if trial expiry date is available
            let trialMessage = "Trial";
            if (record.plan.trialExpiryDate) {
              const expiryDate = new Date(record.plan.trialExpiryDate);
              const today = new Date();
              const daysRemaining = Math.ceil(
                (expiryDate - today) / (1000 * 60 * 60 * 24)
              );

              if (daysRemaining > 0) {
                trialMessage = `Trial (${daysRemaining} days left)`;
              } else {
                trialMessage = "Trial (Expired)";
              }
            }

            return (
              <div>
                <div className="text-blue-500">{trialMessage}</div>
                <button className="text-xs font-semibold text-secondary">
                  Upgrade
                </button>
              </div>
            );

          case "past_due":
            return (
              <div>
                <div className="text-red-500">Payment Overdue</div>
                <button className="text-xs font-semibold text-secondary">
                  Pay Now
                </button>
              </div>
            );

          case "active":
            return (
              <div>
                <div className="text-green-500">Active</div>
              </div>
            );

          case "incomplete":
            return (
              <div>
                <div className="text-yellow-500">Incomplete</div>
                <button className="text-xs font-semibold text-secondary">
                  Complete Setup
                </button>
              </div>
            );

          case "incomplete_expired":
            return (
              <div>
                <div className="text-red-500">Setup Expired</div>
                <button className="text-xs font-semibold text-secondary">
                  Try Again
                </button>
              </div>
            );

          default:
            return (
              <div>
                <div className="text-gray-500">Unknown</div>
              </div>
            );
        }
      },
    },
  ];

  return (
    <Fragment>
      <div className="flex flex-col border dark:border-gray-800 my-5 2xl:my-10 mx-5 2xl:mx-14 rounded-lg shadow-lg dark:bg-[#181818]">
        <div className="w-full flex items-center px-5 py-3">
          <div className="w-full flex gap-5">
            <div className="flex gap-3 items-center w-full xl:w-3/5">
              <div id="step3" className="w-full xl:w-[400px]">
                <Components.SearchField
                  mobilePlaceholder="Search"
                  placeholder="Search User by Name, Email or Phone"
                  value={search}
                  onChange={handleSearchChange}
                />
              </div>
              <Components.SortDropdown
                sortOptions={sortOptions}
                propertyType={true}
                onSortChange={(value) => handleFilter("sortBy", value)}
              />
              <Components.MultiCategoryFilter
                categories={CustomerDefaultMultiFilter}
                onFilterChange={handleFilterChange}
              />
            </div>
            <div className="flex items-center justify-end gap-4 xl:w-1/2">
              <div className="basis-[300px]">
                <Components.Select
                  options={CustomerReportDropdownOptions}
                  defaultValue={"excel"}
                  onChange={handleSelectChange}
                />
              </div>

              <button
                className="text-[1em] rounded-md h-10 flex items-center justify-center px-5 lg:px-3 xl:px-5 font-medium gap-3 lg:gap-1 xl:gap-3 transition-all duration-300 border border-secondary text-secondary"
                onClick={handleExport}
              >
                Export
              </button>

              <Components.TableCustomizeColumnMenu
                setCheckedTableMenuColumn={setCheckedTableMenuColumn}
                checkedTableMenuColumn={checkedTableMenuColumn}
              />
            </div>
          </div>
        </div>

        <div id="step4">
          <Components.CustomTable
            columns={columns.filter(
              (column) =>
                column.dataIndex === "action" ||
                checkedTableMenuColumn.some(
                  (col) => col.dataIndex === column.dataIndex && col.visible
                )
            )}
            dataSource={data?.landlords}
            pagination={true}
            // rowSelection={rowSelection}
            isLoading={loading}
            metaData={data?.metaData}
            setPagination={(pagination) =>
              handleFilter("pagination", pagination)
            }
            pageLimit={filter.limit}
          />
        </div>
      </div>
    </Fragment>
  );
};

export default CustomerReports;
